/**
 * Total upload size the signer is allowed across every file field on a form.
 *
 * The submit endpoints run on Kestrel's default MaxRequestBodySize of 30,000,000 bytes and set no
 * limit of their own, so anything larger is rejected while the request body is read. 20 MiB keeps
 * headroom for multipart boundaries and the `values` JSON, and matches the threshold the old
 * FormGenerator enforced, so the number signers see is unchanged.
 */
export const TOTAL_FILE_SIZE_LIMIT = 20_971_520;

/** Rendered into the size error. Kept beside the limit so the two can't disagree. */
export const TOTAL_FILE_SIZE_LIMIT_LABEL = '20 MB';

/**
 * Identifies the form values that get appended to FormData as files rather than serialized into
 * `values`. Shared with the submit services so the size guard measures exactly what is posted.
 */
export function isFileValue(val: unknown): val is File | File[] {
  return val instanceof File || (Array.isArray(val) && val.every(f => f instanceof File));
}

/** Sums every file across every field of a form value. */
export function totalFileSize(formValue: Record<string, unknown>): number {
  return Object.values(formValue).reduce<number>((total, val) => {
    if (!isFileValue(val)) return total;
    const files = Array.isArray(val) ? val : [val];
    return total + files.reduce((sum, f) => sum + f.size, 0);
  }, 0);
}

/** Formats a byte count for display, e.g. 41943040 -> "40.0 MB". */
export function formatMegabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
